import type { IFileUploader } from '../../types/file_uploader/fileUploader';
import { FileUploaderIcon } from './FileUploaderIcon';
import { Button } from '../Button';
import { UploadedItem } from './UploadedItem';
import { useFileUpload } from './useFileUpload';
import styles from './file-uploader.module.scss';

export const FileUploader = ({
    lang = 'en',
    label,
    labelHint,
    hintText,
    disabled = false,
    loading = false,
    allowedFormats,
    allowedSize, // in MBs
    previouslyUploadedFile,
    error = '',
    multiUpload = false,
    onError,
    onClickHandler,
    onUploadHandler,
    localization,
}: IFileUploader) => {
    const {
        invalid,
        errorMsg,
        uploadedFileInfo,
        uploadedFilesInfo,
        localizedTexts,
        fileUploadHandler,
        handleUploadAgain,
        handleFileDelete,
        handleMultiFileDelete,
        displayAllowedFormats,
        checkFileType,
    } = useFileUpload({
        lang,
        allowedFormats,
        allowedSize,
        previouslyUploadedFile,
        error,
        multiUpload,
        onError,
        onUploadHandler,
        localization
    });

    const acceptAttr = allowedFormats?.map(f => (f.startsWith('.') ? f : `.${f}`)).join(',');

    const uploadContainer = (
        <div
            className={`${styles['file-uploader__upload-container']} ${invalid || errorMsg ? styles['file-uploader__upload-container--invalid'] : ''}`}
            onClick={onClickHandler}
        >
            <FileUploaderIcon icon={'upload'} />
            <div className={styles['file-uploader__upload-container__info-box']}>
                <div className={styles['file-uploader__upload-container__info-box__action-txt']}>
                    <Button
                        size='sm'
                        color='primary'
                        variant='link'
                        text={localizedTexts.clickToUpload}
                        disabled={disabled}
                        onClickHandler={onClickHandler}
                    />
                    <span>{localizedTexts.dragAndDrop}</span>
                </div>
                <div className={styles['file-uploader__upload-container__info-box__supporting-txt']}>
                    {displayAllowedFormats(allowedFormats)}
                    {allowedSize && ` ${localizedTexts.maxSize.replace('{{size}}', String(allowedSize))}`}
                </div>
            </div>
            <input type='file' accept={acceptAttr} title='' onChange={fileUploadHandler} />
        </div>
    );

    const hintOrError = (hintText || errorMsg) && (
        <div className={`${errorMsg ? styles['file-uploader__error-msg'] : styles['file-uploader__hint-text']}`}>
            {errorMsg || hintText}
        </div>
    );

    return (
        <div className={styles['file-uploader']} data-file-uploader-base-disabled={disabled} tabIndex={0}>
            {(label || labelHint) && (
                <div className={styles['file-uploader__label']}>
                    {label}
                    {labelHint && <span className={styles['file-uploader__label-hint']}>{` ${labelHint}`}</span>}
                </div>
            )}
            {multiUpload ? (
                <>
                    {uploadContainer}
                    {hintOrError}
                    {uploadedFilesInfo.map((fileInfo, index) => (
                        <UploadedItem
                            key={index}
                            lang={lang}
                            fileType={checkFileType(fileInfo.name)}
                            fileName={fileInfo.name}
                            fileSize={fileInfo.size?.toString()}
                            state='completed'
                            percentageCompleted={100}
                            onDelete={() => handleMultiFileDelete(index)}
                        />
                    ))}
                </>
            ) : loading ? (
                <UploadedItem
                    lang={lang}
                    fileType={checkFileType(uploadedFileInfo.name)}
                    fileName={uploadedFileInfo.name}
                    fileSize={uploadedFileInfo.size ? `${uploadedFileInfo.size}` : ''}
                    state='loading'
                    percentageCompleted={100}
                />
            ) : uploadedFileInfo.name && !invalid ? (
                error ? (
                    <>
                        <input
                            type='file'
                            id='file-upload-input'
                            title=''
                            style={{ display: 'none' }}
                            accept={acceptAttr}
                            onChange={fileUploadHandler}
                        />
                        <UploadedItem
                            lang={lang}
                            fileType={checkFileType(uploadedFileInfo.name)}
                            fileName={uploadedFileInfo.name}
                            fileSize={uploadedFileInfo.size?.toString()}
                            failureReason={error}
                            state='failed'
                            percentageCompleted={100}
                            uploadAgainHandler={handleUploadAgain}
                        />
                    </>
                ) : (
                    <UploadedItem
                        lang={lang}
                        fileType={checkFileType(uploadedFileInfo.name)}
                        fileName={uploadedFileInfo.name}
                        fileSize={uploadedFileInfo.size?.toString()}
                        state='completed'
                        percentageCompleted={100}
                        onDelete={handleFileDelete}
                    />
                )
            ) : (
                <>
                    {uploadContainer}
                    {hintOrError}
                </>
            )}
        </div>
    );
};
